import {
    LoyaltyCardRedemptionRequestData,
    LoyaltyCardRedemptionService
} from '../loyalty-card/loyalty-card-redemption.service';
import {DealAvailability, DealData, DealEntityModel, DealProductData} from "./deal-entity.model";
import {computed, inject, Injectable, Signal, signal, WritableSignal} from "@angular/core";
import {onEach} from '../utils/observable-utils';
import {MessageDetails, MessageLevel} from "../notification/message/message";
import {NotificationService} from "../notification/notification.service";
import {DataClientError, EntityData} from "@fedelta-pos/data-client";
import {TranslateService} from "@ngx-translate/core";
import {OrderingContext, OrderingContextIntention} from '../ordering-context/ordering-context';
import {OrderingContextModel} from '../ordering-context/ordering-context.model';
import {IdentityModel} from '../identity/identity.model';
import {LoyaltyCardData} from '../identity/member-summary-entity.model';
import {OrderType} from '../order/order';
import {OrderModel} from '../order/order.model';
import {NavigationService} from "../navigation/navigation.service";
import {MenuModel} from '../menu/menu.model';
import {ActiveDealValidationRequestData, ActiveDealValidationService} from "./active-deal-validation.service";
import {ConfigurationModel} from '../configuration/configuration.model';
import {Subject} from 'rxjs';
import {DeferredInjectable} from '../deferred-injection.service';
import {SessionModel} from '../session/session.model';
import {StoreData} from '../store/store-search.model';
import {MenuData} from '../menu/menu-entity.model';

export enum PendingDealStatus {
    NONE,
    PRODUCT_ORDER_REQUIRED,
    ORDERING_PRODUCTS
}

export enum DealResultCode {
    UNAVAILABLE,
    LOADED,
    DECLINED,
    IMMEDIATE,
    DEFERRED
}

export class DealResult {
    constructor(public resultCode: DealResultCode, public msg?: string, public deal?: DealData) {
    }
}

@Injectable({providedIn: "root"})
export class DealModel implements DeferredInjectable {
    private activeDealValidationService: ActiveDealValidationService = inject(ActiveDealValidationService);
    private loyaltyCardRedemptionService: LoyaltyCardRedemptionService = inject(LoyaltyCardRedemptionService);
    private dealEntityModel: DealEntityModel = inject(DealEntityModel);
    private menuModel: MenuModel = inject(MenuModel);
    private configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private sessionModel: SessionModel = inject(SessionModel);
    private orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    private identityModel: IdentityModel = inject(IdentityModel);
    private navigationService: NavigationService = inject(NavigationService);
    private translate: TranslateService = inject(TranslateService);
    private notificationService: NotificationService = inject(NotificationService);
    private orderModel: OrderModel;

    private readonly _appliedDeals: WritableSignal<DealData[]> = signal<DealData[]>([]);
    public readonly appliedDeals: Signal<DealData[]> = this._appliedDeals.asReadonly();
    private readonly _pendingDeal: WritableSignal<DealData> = signal<DealData>(undefined);
    public readonly pendingDeal: Signal<DealData> = this._pendingDeal.asReadonly();
    public readonly pendingDealStatus: WritableSignal<PendingDealStatus> = signal<PendingDealStatus>(PendingDealStatus.NONE);

    public readonly dealCodesAvailable: Signal<boolean> = computed<boolean>(() =>
        this.identityModel.currentMember() && this.identityModel.currentMember().anySelectByDealCodeInApp);
    public readonly dealLimit: Signal<number> = computed<number>(() => this.configurationModel.app()?.dealLimit);
    public readonly dealLimitReached: Signal<boolean> = computed<boolean>(() =>
        this.configurationModel.app()?.dealLimit && this._appliedDeals().length >= this.configurationModel.app()?.dealLimit);

    public readonly shouldApplyRsaToDeal: Signal<(deal: DealData) => boolean> = computed<(deal: DealData) => boolean>(() => {
        const currentStore: StoreData = this.orderingContextModel.currentStore();
        const currentMenu: MenuData = this.menuModel.currentMenu();
        const rsaAppliesToSellable: (sellableId: number) => boolean = this.menuModel.rsaAppliesToSellable();
        return (deal: DealData): boolean =>
            currentStore && currentStore.rsaMinimumAge && currentMenu &&
            deal.products.some((dealProduct: DealProductData) => rsaAppliesToSellable(dealProduct.sellable.sellableId));
    });

    public readonly hasOrderedIncludedSellables: Signal<(deal: DealData) => boolean> = computed<(deal: DealData) => boolean>(() => {
        const personallyOrderedSellableIds: Set<number> = this.orderModel.personallyOrderedSellableIds();
        return (deal: DealData): boolean =>
            deal.products.some((product: DealProductData) => personallyOrderedSellableIds.has(product.sellable.sellableId));
    });

    public readonly dealIsSuppressed: Signal<(deal: DealData) => boolean> = computed<(deal: DealData) => boolean>(() => {
        const hasOrderedAnyItemPersonally: boolean = this.orderModel.hasOrderedAnyItemPersonally();
        const hasOrderedIncludedSellables: (deal: DealData) => boolean = this.hasOrderedIncludedSellables();
        return (deal: DealData): boolean =>
            deal.suppressDealCardIfAnyOrdered ? hasOrderedAnyItemPersonally
                : deal.suppressDealCardIfOrdered ? hasOrderedIncludedSellables(deal)
                    : false;
    });

    public readonly findAppliedDealById: Signal<(dealId: string) => DealData> = computed<(dealId: string) => DealData>(() => {
        const appliedDeals: DealData[] = this._appliedDeals();
        return (dealId: string): DealData =>
            appliedDeals.find((deal: DealData) => deal.id === dealId);
    });

    /**
     * Notifies a change in the status of deals.
     */
    public readonly dealStatusChanged$: Subject<void> = new Subject();

    constructor() {
        console.log("--- DealModel");

        onEach(this.sessionModel.sessionReset$, () => this.removeAllDeals());
    }

    public inject(): void {
        this.orderModel = inject(OrderModel);
    }

    public notifyDealStatusChanged() {
        this.dealStatusChanged$.next();
    }

    public getDealName(deal: DealData, useDefault: boolean = true) {
        return !deal ? undefined : (deal.title || deal.name || (useDefault ? this.translate.instant("woa.label.order.deal") : undefined));
    }

    public applyDeal(deal: DealData): void {
        if (this._appliedDeals().some((appliedDeal: DealData) => appliedDeal.id === deal.id)) {
            return;
        }

        this._appliedDeals.set([...this._appliedDeals(), deal]);
        this._pendingDeal.set(undefined);
        this.pendingDealStatus.set(PendingDealStatus.NONE);
        this.notificationService.displayFooterMessage(new MessageDetails(
            MessageLevel.SUCCESS, "woa.message.deals.applied", {"dealName": this.getDealName(deal)}, "tags"));
        this.notifyDealStatusChanged();
    }

    public cancelPendingDeal(): void {
        if (this._pendingDeal()) {
            const dealId: string = this._pendingDeal().id;
            this._pendingDeal.set(undefined);
            this.pendingDealStatus.set(PendingDealStatus.NONE);

            // Remove the deal items from the order and save the order.
            // NB: This must be done before notifying the deal status change, or else we can end up saving
            // a new order during the load of the ordering page, and thus preventing the current order from being restored.
            this.orderModel.removeDealItems([dealId]);
            this.notifyDealStatusChanged();
        }
    }

    public removeDeal(deal: DealData): void {
        this.removeDeals((appliedDeal: DealData) => appliedDeal === deal);
    }

    public removeAllDeals(): void {
        this._pendingDeal.set(undefined);
        this.pendingDealStatus.set(PendingDealStatus.NONE);
        this.removeDeals(() => true);
    }

    public removeDeals(predicate: (deal: DealData) => boolean): void {
        const dealIdsToRemove: string[] = [];
        this._appliedDeals.update(appliedDeals => appliedDeals.filter((deal: DealData) => {
            if (predicate(deal)) {
                dealIdsToRemove.push(deal.id);
                return false;
            }
            return true;
        }));
        if (dealIdsToRemove.length) {

            // Remove the deals from the order and save the order.
            // NB: This must be done before notifying the deal status change, or else we can end up saving
            // a new order during the load of the ordering page, and thus preventing the current order from being restored.
            this.orderModel.removeDealItems(dealIdsToRemove);
            this.notifyDealStatusChanged();
        }
    }

    /**
     * Looks up a deal by the specified deal code, and starts ordering it.
     * @return DealStatus indicating if the deal is immediately ordered (IMMEDIATE), cannot be ordered (INVALID), or will be ordered after proceeding to the ordering page (DEFERRED)
     * @param dealCode
     */
    public async orderDealByDealCode(dealCode: string): Promise<DealResult> {
        const result: DealResult = await this.loadDealByDealCode(dealCode);
        return result.resultCode === DealResultCode.LOADED ? this.orderDeal(result.deal) : result;
    }

    /**
     * Starts ordering the specified deal.
     * @return DealResult indicating the result of attempting to order the deal
     * @param deal
     */
    public async orderDeal(deal: DealData): Promise<DealResult> {

        // If the deal availability has already been assessed, notify the user immediately of any issues
        let unavailableReason: string = this.getDealUnavailableReason(deal, deal.availability);
        if (unavailableReason) {
            return new DealResult(DealResultCode.UNAVAILABLE, unavailableReason);
        }

        // Check that the deal has not already been ordered
        if (this.findAppliedDealById()(deal.id)) {
            return new DealResult(DealResultCode.UNAVAILABLE, "woa.heading.order.dealAlreadyOnOrder");
        }

        // If the deal limit is 1, and there is a current deal, confirm that the user wants to replace it.
        if (this.configurationModel.app()?.dealLimit == 1 && this._appliedDeals().length >= 1) {

            // Perform RSA verification if necessary for the selected deal before prompting for replacement
            if (this.shouldApplyRsaToDeal()(deal) && !await this.orderModel.verifyRsa()) {
                return new DealResult(DealResultCode.DECLINED);
            }

            if (!await this.notificationService.displayConfirmation("woa.heading.order.confirmReplaceDeal", undefined,
                "woa.message.order.confirmReplaceSingleDeal",
                "woa.button.common.noThanks", "woa.button.deals.orderDeal", false)) {
                return new DealResult(DealResultCode.DECLINED);
            }

            this.removeAllDeals();
        }

        // Only one deal with a direct discount is allowed per order.
        // If there is an incompatible deal already applied, confirm that the user wants to replace it.
        if (!!deal.discount.discountId) {
            const incompatibleDeal: DealData = this._appliedDeals().find((appliedDeal: DealData) => !!appliedDeal.discount.discountId);
            if (incompatibleDeal) {

                // Perform RSA verification if necessary for the selected deal before prompting for replacement
                if (this.shouldApplyRsaToDeal()(deal) && !await this.orderModel.verifyRsa()) {
                    return new DealResult(DealResultCode.DECLINED);
                }

                if (!await this.notificationService.displayConfirmation("woa.heading.order.confirmReplaceDeal", undefined,
                    "woa.message.order.confirmReplaceIncompatibleDeal",
                    "woa.button.common.noThanks", "woa.button.deals.orderDeal", false,
                    {incompatibleDealName: this.getDealName(incompatibleDeal)})) {
                    return new DealResult(DealResultCode.DECLINED);
                }

                this.removeDeal(incompatibleDeal);
            }
        }

        // If the deal limit has been reached, then other than for the limit = 1 case above,
        // we require the user to manually remove one or more deals and try again.
        if (this.configurationModel.app()?.dealLimit && this._appliedDeals().length >= this.configurationModel.app()?.dealLimit) {
            if (await this.notificationService.displayConfirmation("woa.heading.order.dealLimitReached", undefined,
                "woa.message.order.dealLimitReached",
                "woa.button.common.cancel", "woa.button.deals.showActiveDeals", false,
                {limit: this.configurationModel.app()?.dealLimit})) {
                void this.navigationService.createPage("woa-deal-code-entry-page");
            }
            return new DealResult(DealResultCode.DECLINED);
        }

        // We force store and order type selection before applying a deal.
        // This method will be called again when and if the ordering context is applied.
        // Note that the deal availability is cleared as it will have to be reassessed once the selection has been made.
        if (!this.orderingContextModel.currentStore() || !this.orderingContextModel.orderType()) {
            deal.availability = undefined;
            await this.orderingContextModel.selectAndApplyStoreAndOrderType(new OrderingContext()
                .withIntention(OrderingContextIntention.ORDER)
                .withSelectedTable(this.orderingContextModel.preselectedTableSelection()));
            if (!this.orderingContextModel.currentStore() || !this.orderingContextModel.orderType()) {
                return new DealResult(DealResultCode.DECLINED);
            }
        }

        // If the deal is not selected from a deal code, or if a store or order type selection has been forced above, the deal availability will not be known.
        // In that case, we must assess/reassess whether the deal is valid for the currently selected store and order type.
        if (!deal.availability) {
            try {
                const availability: DealAvailability = await this.getDealAvailability(deal);
                let unavailableReason: string = this.getDealUnavailableReason(deal, availability);
                if (unavailableReason) {
                    return new DealResult(DealResultCode.UNAVAILABLE, unavailableReason);
                }
            } catch (error) {
                await this.notificationService.displayAlert(null, "woa.message.order.dealLookupFailed");
                console.error(error);
                throw error;
            }
        }

        // Perform RSA verification  if necessary for the selected deal before applying
        if (this.shouldApplyRsaToDeal()(deal) && !await this.orderModel.verifyRsa()) {
            return new DealResult(DealResultCode.DECLINED);
        }

        // Apply the deal
        if (deal.products.length === 0) {
            this.applyDeal(deal);
            return new DealResult(DealResultCode.IMMEDIATE);
        } else {
            this._pendingDeal.set(deal);
            this.pendingDealStatus.set(PendingDealStatus.PRODUCT_ORDER_REQUIRED);
            this.notifyDealStatusChanged();
            return new DealResult(DealResultCode.DEFERRED);
        }
    }

    private async loadDealByDealCode(dealCode: string): Promise<DealResult> {
        try {
            await this.dealEntityModel.loadEntity(dealCode, {localDateTime: EntityData.formatLocalDateTime(this.configurationModel.now)});
            return new DealResult(DealResultCode.LOADED, undefined, this.dealEntityModel.dataSignal());
        } catch (error) {
            if (error instanceof DataClientError && error.status === 404) {
                return new DealResult(DealResultCode.UNAVAILABLE, "woa.message.order.dealNotValid");
            }
            await this.notificationService.displayAlert(null, "woa.message.order.dealLookupFailed");
            console.error(error);
            throw error;
        }
    }

    private async getDealAvailability(deal: DealData): Promise<DealAvailability> {
        const activeDealValidationRequest: ActiveDealValidationRequestData = <ActiveDealValidationRequestData>EntityData.create(ActiveDealValidationRequestData);
        activeDealValidationRequest.dealId = deal.id;
        activeDealValidationRequest.integratedDealConfigId = deal.integratedDealConfigId;
        activeDealValidationRequest.providerName = deal.providerName;
        activeDealValidationRequest.localDateTime = EntityData.formatLocalDateTime(this.configurationModel.now);
        const result: any = await this.activeDealValidationService.performAction(activeDealValidationRequest);
        return result["availability"];
    }

    /**
     * Returns a message for display to the user (if necessary) for the specified deal availability.
     * For unavailability due to current store or order type, no notification is required if a store and order type are not yet selected.
     * This is because store and order type selection will be required, after which the availability will be reassessed.
     *
     * @private
     * @param data
     * @param availability
     */
    private getDealUnavailableReason(data: DealData, availability: DealAvailability): string {
        switch (availability) {
            case DealAvailability.INVALID:
                return "woa.message.order.dealNotValid";
            case DealAvailability.EXPIRED:
                return "woa.message.order.dealExpired";
            case DealAvailability.CONSUMED:
                return "woa.message.order.dealConsumed";
            case DealAvailability.DISCONTINUED:
                return "woa.message.order.dealDiscontinued";
            case DealAvailability.UNAVAILABLE_AT_STORE:
                if (data.providerName || (this.orderingContextModel.currentStore() && this.orderingContextModel.orderType())) {
                    return "woa.message.order.dealNotAvailableAtStore";
                }
                return undefined;
            case DealAvailability.UNSCHEDULED_AT_STORE:
                if (data.providerName || (this.orderingContextModel.currentStore() && this.orderingContextModel.orderType())) {
                    return "woa.message.order.dealNotScheduledAtStore";
                }
                return undefined;
            case DealAvailability.BEFORE_VALID_PERIOD:
                return "woa.message.order.dealBeforeValidPeriod";
            case DealAvailability.OUTSIDE_WEEKDAY_TIME_RESTRICTION:
                return "woa.message.order.dealOutsideWeekdayTimeRestriction";
            default:
                if (!data.isAvailableForOrderType(this.orderingContextModel.orderType())) {
                    switch (this.orderingContextModel.orderType()) {
                        case OrderType.ORDER_AHEAD:
                            return "woa.message.order.dealNotAvailableOrderAhead";
                        case OrderType.ORDER_DELIVERY:
                            return "woa.message.order.dealNotAvailableOrderDelivery";
                        case OrderType.ORDER_AT_TABLE:
                            return "woa.message.order.dealNotAvailableOrderAtTable";
                    }
                }
                return undefined;
        }
    }

    public async redeemLoyaltyCard(loyaltyCard: LoyaltyCardData): Promise<unknown> {
        const loyaltyCardRedemptionRequest: LoyaltyCardRedemptionRequestData = <LoyaltyCardRedemptionRequestData>EntityData.create(LoyaltyCardRedemptionRequestData);
        loyaltyCardRedemptionRequest.loyaltyCardId = loyaltyCard.id;
        loyaltyCardRedemptionRequest.integratedLoyaltyConfigId = loyaltyCard.integratedLoyaltyConfigId;
        loyaltyCardRedemptionRequest.providerName = loyaltyCard.providerName;
        try {
            return this.loyaltyCardRedemptionService.performAction(loyaltyCardRedemptionRequest);
        } catch (error) {
            return this.notificationService.displayFooterError(error, "woa.message.order.loyaltyCardRedemptionFailed");
        }
    }
}
